// The order card beside a conversation (owner, 2026-09-26): status, client,
// address, price, 3D link, editor — and the next thing to do, one click away.
//
// Read-only. A recommended message is written into the reply box, never sent:
// the person reads it and presses send. The rules are in ./orderCard.ts.
import { useEffect, useState } from 'react';
import { PaymentRequestButton } from '@/components/admin/PaymentRequestModal';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Check, Copy, ExternalLink, Eye, MapPin, MessageSquareText, PenLine, Phone, TriangleAlert,
} from 'lucide-react';
import { whatsappApi, type ConversationOrder } from '@/lib/api';
import { ChatActionsCard } from './ChatActionsCard';
import { useT } from '@/lib/i18n';
import { ORDER_STATUS_COLORS, ORDER_STATUS_LABELS, formatDate } from '@/lib/utils';
import { ClientRequestsCard } from '@/components/admin/ClientRequestsCard';
import { useCan } from '@/lib/actions';
import { editorUrl, money, orderUrl, pagesLine, paymentLine, recommendedActions, type OrderAction } from './orderCard';

const btn = 'inline-flex items-center justify-center gap-1.5 rounded-lg text-[12.5px] font-medium min-h-11 md:min-h-9 px-3 transition-colors duration-150 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1';
const primaryBtn = `${btn} bg-brand-500 text-white hover:bg-brand-600`;
const secondaryBtn = `${btn} bg-white text-gray-700 border border-gray-200 hover:bg-gray-50`;
const iconBtn = 'inline-flex items-center justify-center h-9 w-9 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors duration-150 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500';

function useCopy() {
  const [copied, setCopied] = useState<string | null>(null);
  useEffect(() => {
    if (!copied) return;
    const id = window.setTimeout(() => setCopied(null), 1600);
    return () => window.clearTimeout(id);
  }, [copied]);
  const copy = async (key: string, value: string) => {
    try { await navigator.clipboard.writeText(value); setCopied(key); } catch { /* clipboard refused: nothing to show */ }
  };
  return { copied, copy };
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="pt-3 mt-3 border-t border-gray-100 first:border-0 first:pt-0 first:mt-0">
      <h3 className="text-[12px] font-semibold text-gray-500 mb-2">{title}</h3>
      {children}
    </section>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1">
      <dt className="text-[12px] text-gray-500 shrink-0">{label}</dt>
      <dd className="text-[13px] text-gray-900 text-right min-w-0 break-words">{children}</dd>
    </div>
  );
}

function ActionButton({ a, onCompose }: { a: OrderAction; onCompose: (text: string) => void }) {
  const { t } = useT();
  const cls = a.primary ? primaryBtn : secondaryBtn;
  if (a.kind === 'compose') {
    return (
      <button type="button" className={`${cls} w-full`} onClick={() => onCompose(a.text)}>
        <MessageSquareText className="h-4 w-4 shrink-0" aria-hidden />
        {t(a.label)}
      </button>
    );
  }
  return (
    <a href={a.href} target="_blank" rel="noopener noreferrer" className={`${cls} w-full`}>
      {a.id === 'open-3d' ? <Eye className="h-4 w-4 shrink-0" aria-hidden />
        : a.id === 'editor' ? <PenLine className="h-4 w-4 shrink-0" aria-hidden />
        : <ExternalLink className="h-4 w-4 shrink-0" aria-hidden />}
      {t(a.label)}
    </a>
  );
}

function PreviewState({ o }: { o: ConversationOrder }) {
  const { t } = useT();
  if (!o.preview_url) return <span className="text-[12px] text-gray-500">{t('Pas de lien')}</span>;
  // « Envoyé par l'équipe · lu », « Nouvelle version pas encore annoncée »… —
  // the worker's ONE definition (lib/previewState.ts), drawn as is.
  const pv = o.preview ?? null;
  if (pv) {
    const tone = pv.chip_state === 'done' ? 'green' : pv.chip_state === 'warn' ? 'amber' : 'gray';
    const text = { green: 'text-green-700', amber: 'text-amber-700', gray: 'text-gray-600' }[tone];
    const dot = { green: 'bg-green-500', amber: 'bg-amber-500', gray: 'bg-gray-400' }[tone];
    return (
      <span className={`inline-flex items-center gap-1.5 text-[12px] font-medium ${text}`} data-testid="preview-state">
        <span className={`h-2 w-2 rounded-full ${dot}`} aria-hidden />
        {t(pv.label)}
      </span>
    );
  }
  const ready = o.previews_status === 'ready';
  return (
    <span className={`inline-flex items-center gap-1.5 text-[12px] font-medium ${ready ? 'text-green-700' : 'text-amber-700'}`}>
      <span className={`h-2 w-2 rounded-full ${ready ? 'bg-green-500' : 'bg-amber-500'}`} aria-hidden />
      {ready ? t('Prêt') : o.previews_status === 'failed' ? t('Échec du rendu') : t('En préparation')}
    </span>
  );
}

export function OrderPanel({ chatId, onCompose }: { chatId: string; onCompose: (text: string) => void }) {
  const { t } = useT();
  const { copied, copy } = useCopy();
  const canEditAlbum = useCan()('order.agent_album_edits');
  const [composed, setComposed] = useState(false);
  // The order the switcher shows (2026-10-10): null = the one the worker picks
  // (her newest order still alive).
  const [picked, setPicked] = useState<string | null>(null);
  useEffect(() => { setComposed(false); setPicked(null); }, [chatId]);
  const qc = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['whatsapp-conversation-order', chatId, picked],
    queryFn: () => whatsappApi.conversationOrder(chatId, picked),
    staleTime: 30_000,
    refetchInterval: 60_000,
    placeholderData: (prev) => prev,
  });
  const link = useMutation({
    mutationFn: (shopifyOrderId: string) => whatsappApi.linkConversationOrder(chatId, shopifyOrderId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['whatsapp-conversation-order', chatId] });
      void qc.invalidateQueries({ queryKey: ['whatsapp-conversations'] });
    },
  });

  const shell = 'bg-white rounded-xl border border-gray-200 p-4';

  if (isLoading) {
    return (
      <div className={shell} aria-busy="true" aria-label={t('Chargement de la commande')}>
        <div className="h-4 w-24 rounded bg-gray-100 animate-pulse" />
        <div className="h-3 w-40 rounded bg-gray-100 animate-pulse mt-3" />
        <div className="h-11 w-full rounded-lg bg-gray-100 animate-pulse mt-4" />
        <div className="h-3 w-full rounded bg-gray-100 animate-pulse mt-4" />
        <div className="h-3 w-2/3 rounded bg-gray-100 animate-pulse mt-2" />
      </div>
    );
  }
  if (isError) {
    return <div className={`${shell} text-[12.5px] text-red-700`} role="alert">{t('La commande n’a pas pu être chargée. Réessayez dans un instant.')}</div>;
  }

  const o = data?.order ?? null;
  if (!o) {
    return (
      <div className={shell}>
        <p className="text-[13px] font-medium text-gray-800">{t('Aucune commande liée à cette conversation.')}</p>
        <p className="text-[12px] text-gray-500 mt-1 leading-snug">{t('Cherchez-la par numéro de commande ou par téléphone dans la barre de recherche.')}</p>
      </div>
    );
  }

  const actions = recommendedActions(o);
  const albumsLabel = o.albums.length > 1 ? t('{n} albums', { n: String(o.albums.length) }) : t('1 album');
  const fullAddress = [o.address, o.city].filter(Boolean).join(', ');

  // Every order of hers, newest first — the one shown is highlighted.
  const allOrders = [{ shopify_order_id: o.shopify_order_id, name: o.name, status: o.status, created_at: o.created_at }, ...(data?.other_orders ?? [])]
    .sort((a, b) => String(b.created_at ?? '').localeCompare(String(a.created_at ?? '')));

  return (
    <div className={shell}>
      {/* ─── Her orders: pick which one (#MAN-082802, 2026-10-10) ────────── */}
      {allOrders.length > 1 && (
        <div className="mb-3">
          <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500 mb-1.5">
            {t('{n} commandes de ce client', { n: String(allOrders.length) })}
          </p>
          <div className="flex flex-wrap gap-1.5" role="tablist" aria-label={t('Commandes du client')}>
            {allOrders.map((x) => {
              const on = x.shopify_order_id === o.shopify_order_id;
              return (
                <button key={x.shopify_order_id} type="button" role="tab" aria-selected={on}
                  onClick={() => setPicked(x.shopify_order_id)}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 min-h-8 text-[12px] tabular-nums focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${on ? 'border-brand-500 bg-brand-50 text-gray-900 font-semibold' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                  <span>{x.name}</span>
                  <span className="text-[11px] font-normal text-gray-500">{t(ORDER_STATUS_LABELS[x.status] ?? x.status)}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ─── Which order, where it stands ─────────────────────────────────── */}
      <div className="flex items-start justify-between gap-3">
        <a href={orderUrl(o.shopify_order_id)} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-[16px] font-semibold text-gray-900 hover:text-brand-700 tabular-nums rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500">
          {o.name}
          <ExternalLink className="h-3.5 w-3.5 text-gray-400" aria-hidden />
          <span className="sr-only">{t('Ouvrir la commande')}</span>
        </a>
        <span className={`shrink-0 text-[11.5px] font-semibold px-2 py-0.5 rounded-full border ${ORDER_STATUS_COLORS[o.status] ?? 'bg-gray-100 text-gray-700 border-gray-200'}`}>
          {t(ORDER_STATUS_LABELS[o.status] ?? o.status)}
        </span>
      </div>
      <p className="text-[12px] text-gray-500 mt-0.5">
        {[o.bundle, albumsLabel, o.created_at ? t('commandée le {d}', { d: formatDate(o.created_at) }) : null].filter(Boolean).join(' · ')}
      </p>
      {data?.matched_by === 'phone' && (
        <div className="mt-2 text-[12px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5">
          <p className="flex items-start gap-1.5">
            <TriangleAlert className="h-3.5 w-3.5 mt-0.5 shrink-0" aria-hidden />
            {t('Trouvée par le numéro de téléphone — pas encore liée à cette conversation.')}
          </p>
          <button type="button" onClick={() => link.mutate(o.shopify_order_id)} disabled={link.isPending}
            className="mt-1.5 w-full rounded-md border border-amber-300 bg-white px-2 min-h-9 text-[12.5px] font-semibold text-amber-900 hover:bg-amber-100 disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500">
            {link.isPending ? t('Liaison…') : t('Lier cette conversation à {name}', { name: o.name })}
          </button>
          {link.isError && <p className="mt-1 text-red-700" role="alert">{(link.error as Error).message}</p>}
        </div>
      )}

      {/* ─── The next thing to do ─────────────────────────────────────────── */}
      <div className="mt-3 space-y-2">
        {actions.map((a) => (
          <ActionButton key={a.id} a={a} onCompose={(text) => { onCompose(text); setComposed(true); }} />
        ))}
        {/* Owner, 2026-10-14: « i want that button to be here » — with the
            order's other actions, for the order this card shows. */}
        <PaymentRequestButton target={{ order: o.shopify_order_id }} className="w-full justify-center" />
        <p className="text-[11.5px] text-gray-500 leading-snug min-h-[1rem]" aria-live="polite">
          {composed ? t('Message prêt dans la zone de réponse — relisez-le, puis envoyez.') : ''}
        </p>
      </div>

      {/* ─── The client's requests + « L'agent modifie l'album » (2026-10-03) ─
          Right under the next thing to do: when « album edited by hand »
          arrives, this is where the agent is turned back on. Same component
          as the order page. Renders nothing without editor.access. */}
      {o.shopify_order_id && canEditAlbum && (
        <div className="pt-3 mt-3 border-t border-gray-100">
          <ClientRequestsCard shopifyOrderId={o.shopify_order_id} variant="section" previewUrl={o.preview_url} />
        </div>
      )}

      {/* ─── Client ─────────────────────────────────────────────────────── */}
      <Section title={t('Client')}>
        <p className="text-[13.5px] font-medium text-gray-900">{o.customer_name ?? t('Sans nom')}</p>
        {o.customer_phone && (
          <div className="flex items-center gap-1 mt-0.5">
            <span className="text-[13px] text-gray-700 tabular-nums flex-1">{o.customer_phone}</span>
            <a href={`tel:+${o.customer_phone.replace(/\D/g, '')}`} className={iconBtn} aria-label={t('Appeler')} title={t('Appeler')}>
              <Phone className="h-4 w-4" aria-hidden />
            </a>
            <button type="button" className={iconBtn} onClick={() => copy('phone', o.customer_phone ?? '')}
              aria-label={t('Copier le numéro')} title={t('Copier le numéro')}>
              {copied === 'phone' ? <Check className="h-4 w-4 text-green-600" aria-hidden /> : <Copy className="h-4 w-4" aria-hidden />}
            </button>
          </div>
        )}
        <div className="flex items-start gap-1 mt-1.5">
          <MapPin className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" aria-hidden />
          <p className="text-[13px] text-gray-700 flex-1 leading-snug">
            {o.city && <span className="font-medium text-gray-900">{o.city}</span>}
            {o.city && o.address ? <br /> : null}
            {o.address ?? (o.city ? null : <span className="text-gray-500">{t('Adresse pas encore donnée')}</span>)}
          </p>
          {fullAddress && (
            <button type="button" className={iconBtn} onClick={() => copy('address', fullAddress)}
              aria-label={t('Copier l’adresse')} title={t('Copier l’adresse')}>
              {copied === 'address' ? <Check className="h-4 w-4 text-green-600" aria-hidden /> : <Copy className="h-4 w-4" aria-hidden />}
            </button>
          )}
        </div>
      </Section>

      {/* ─── Money and pages ─────────────────────────────────────────────── */}
      <Section title={t('Commande')}>
        <dl>
          <Row label={t('Prix total')}><span className="font-semibold tabular-nums">{money(o.total_price)}</span></Row>
          <Row label={t('Pages')}><span className="tabular-nums">{pagesLine(o, t)}</span></Row>
          <Row label={t('Paiement')}>{paymentLine(o, t)}</Row>
          {o.parcel_code && <Row label={t('Livraison')}><span className="tabular-nums">{o.parcel_code}</span>{o.parcel_status ? ` · ${o.parcel_status}` : ''}</Row>}
        </dl>
      </Section>

      {/* ─── The album ───────────────────────────────────────────────────── */}
      <Section title={t('Album')}>
        <div className="flex items-center justify-between gap-2">
          <span className="text-[12px] text-gray-500">{t('Aperçu 3D')}</span>
          <PreviewState o={o} />
        </div>
        {o.preview_url && (
          <div className="flex gap-2 mt-2">
            <a href={o.preview_url} target="_blank" rel="noopener noreferrer" className={`${secondaryBtn} flex-1`}>
              <Eye className="h-4 w-4" aria-hidden />{t('Ouvrir')}
            </a>
            <button type="button" className={`${secondaryBtn} flex-1`} onClick={() => copy('preview', o.preview_url ?? '')}>
              {copied === 'preview' ? <Check className="h-4 w-4 text-green-600" aria-hidden /> : <Copy className="h-4 w-4" aria-hidden />}
              {copied === 'preview' ? t('Copié') : t('Copier le lien')}
            </button>
          </div>
        )}
        <div className="grid grid-cols-1 gap-2 mt-2">
          {o.albums.map((n) => (
            <a key={n} href={editorUrl(o.shopify_order_id, n)} target="_blank" rel="noopener noreferrer" className={secondaryBtn}>
              <PenLine className="h-4 w-4" aria-hidden />
              {o.albums.length > 1 ? t('Éditeur · album {n}', { n: String(n) }) : t('Ouvrir l’éditeur')}
            </a>
          ))}
        </div>
      </Section>

      {/* ─── The client's other orders ───────────────────────────────────── */}

      {/* What the agent did here — Annuler / Valider (agent v2 `actions`). */}
      <ChatActionsCard chatId={chatId} />
    </div>
  );
}
